import React from "react";
import {
  Badge,
  Box,
  Checkbox,
  Divider,
  Group,
  NumberInput,
  Radio,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import {
  IconBuilding,
  IconPageBreak,
  IconPhoto,
  IconUser,
} from "@tabler/icons-react";
import { ACTOR_COLOR, ACTOR_LABEL } from "./constants";
import {
  ChoiceConfig,
  DateConfig,
  FieldActor,
  FormTemplateField,
  LongTextConfig,
  NumberConfig,
  SectionDescriptionConfig,
  SectionHeaderConfig,
  ShortTextConfig,
} from "../../types/formTemplates";
import { formatLocalDate, parseLocalDate } from "../../utils/localDateUtils";

export interface FieldRendererProps {
  field: FormTemplateField;
  mode: "preview" | "edit";
  value?: unknown;
  onChange?: (value: unknown) => void;
  error?: string;
}

const ActorBadge: React.FC<{ actor: FieldActor }> = ({ actor }) => (
  <Badge
    size="xs"
    variant="light"
    color={ACTOR_COLOR[actor]}
    leftSection={
      actor === "staff" ? <IconBuilding size={10} /> : <IconUser size={10} />
    }
  >
    {ACTOR_LABEL[actor]}
  </Badge>
);

function FieldLabel({ field }: { field: FormTemplateField }) {
  return (
    <Group gap="xs" align="center" mb={4}>
      <Text size="sm" fw={500}>
        {field.label}
        {field.required && (
          <Text span c="red" ml={4}>
            *
          </Text>
        )}
      </Text>
      {field.actor && <ActorBadge actor={field.actor} />}
    </Group>
  );
}

export const FieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  mode,
  value,
  onChange,
  error,
}) => {
  const disabled = mode === "preview";

  if (field.type === "section_header") {
    const cfg = (field.config ?? {}) as Partial<SectionHeaderConfig>;
    return (
      <Title order={4} mt="md" mb="xs">
        {cfg.text || field.label}
      </Title>
    );
  }

  if (field.type === "section_description") {
    const cfg = (field.config ?? {}) as Partial<SectionDescriptionConfig>;
    return (
      <Text c="dimmed" size="sm" style={{ whiteSpace: "pre-wrap" }}>
        {cfg.text || ""}
      </Text>
    );
  }

  if (field.type === "divider") {
    return <Divider my="sm" />;
  }

  if (field.type === "page_break") {
    return (
      <Group
        gap="xs"
        align="center"
        py="xs"
        style={{
          borderTop: "1px dashed var(--mantine-color-gray-4)",
          borderBottom: "1px dashed var(--mantine-color-gray-4)",
          background: "var(--mantine-color-gray-0)",
          padding: "8px 12px",
          borderRadius: 4,
          justifyContent: "center",
          color: "var(--mantine-color-gray-6)",
        }}
      >
        <IconPageBreak size={14} />
        <Text size="xs" fw={500} tt="uppercase" c="dimmed">
          Page break
        </Text>
      </Group>
    );
  }

  const commonProps = { disabled, error } as const;

  let control: React.ReactNode = null;

  switch (field.type) {
    case "short_text": {
      const cfg = (field.config ?? {}) as Partial<ShortTextConfig>;
      control = (
        <TextInput
          {...commonProps}
          placeholder={cfg.placeholder || ""}
          maxLength={cfg.maxLength ?? undefined}
          value={(value as string) ?? ""}
          onChange={(e) => onChange?.(e.currentTarget.value)}
        />
      );
      break;
    }
    case "long_text": {
      const cfg = (field.config ?? {}) as Partial<LongTextConfig>;
      control = (
        <Textarea
          {...commonProps}
          placeholder={cfg.placeholder || ""}
          maxLength={cfg.maxLength ?? undefined}
          minRows={3}
          value={(value as string) ?? ""}
          onChange={(e) => onChange?.(e.currentTarget.value)}
        />
      );
      break;
    }
    case "number": {
      const cfg = (field.config ?? {}) as Partial<NumberConfig>;
      control = (
        <NumberInput
          {...commonProps}
          min={cfg.min ?? undefined}
          max={cfg.max ?? undefined}
          decimalScale={cfg.decimalPlaces ?? undefined}
          value={(value as number) ?? ""}
          onChange={(v) => onChange?.(typeof v === "number" ? v : null)}
        />
      );
      break;
    }
    case "date": {
      const cfg = (field.config ?? {}) as Partial<DateConfig>;
      const dateValue =
        typeof value === "string" ? parseLocalDate(value) : null;
      control = (
        <DateInput
          {...commonProps}
          highlightToday
          minDate={
            cfg.minDate ? (parseLocalDate(cfg.minDate) ?? undefined) : undefined
          }
          maxDate={
            cfg.maxDate ? (parseLocalDate(cfg.maxDate) ?? undefined) : undefined
          }
          value={dateValue}
          onChange={(d) => {
            if (!d) return onChange?.(null);
            const asDate =
              typeof d === "string"
                ? (parseLocalDate(d) ?? new Date(d))
                : typeof d === "number"
                  ? new Date(d)
                  : (d as Date);
            onChange?.(formatLocalDate(asDate));
          }}
          placeholder="Pick a date"
        />
      );
      break;
    }
    case "single_choice": {
      const cfg = (field.config ?? { options: [] }) as Partial<ChoiceConfig>;
      const options = cfg.options ?? [];
      control = (
        <Radio.Group
          value={(value as string) ?? ""}
          onChange={(v) => onChange?.(v)}
          error={error}
        >
          <Stack gap="xs">
            {options.map((opt) => (
              <Radio
                key={opt.id}
                value={opt.id}
                label={opt.label}
                disabled={disabled}
              />
            ))}
          </Stack>
        </Radio.Group>
      );
      break;
    }
    case "multiple_choice": {
      const cfg = (field.config ?? { options: [] }) as Partial<ChoiceConfig>;
      const options = cfg.options ?? [];
      const current = Array.isArray(value) ? (value as string[]) : [];
      control = (
        <Checkbox.Group
          value={current}
          onChange={(v) => onChange?.(v)}
          error={error}
        >
          <Stack gap="xs">
            {options.map((opt) => (
              <Checkbox
                key={opt.id}
                value={opt.id}
                label={opt.label}
                disabled={disabled}
              />
            ))}
          </Stack>
        </Checkbox.Group>
      );
      break;
    }
    case "dropdown": {
      const cfg = (field.config ?? { options: [] }) as Partial<ChoiceConfig>;
      const options = cfg.options ?? [];
      control = (
        <Select
          {...commonProps}
          data={options.map((o) => ({ value: o.id, label: o.label }))}
          value={(value as string) ?? null}
          onChange={(v) => onChange?.(v)}
          placeholder="Select an option"
        />
      );
      break;
    }
    case "image_upload":
      control = (
        <Box
          p="md"
          style={{
            border: "2px dashed var(--mantine-color-gray-3)",
            borderRadius: 8,
            textAlign: "center",
            background: "var(--mantine-color-gray-0)",
          }}
        >
          <IconPhoto
            size={32}
            color="var(--mantine-color-gray-5)"
            aria-hidden
          />
          <Text c="dimmed" size="xs" mt={4}>
            {mode === "preview"
              ? "Image upload preview"
              : "Click to upload image"}
          </Text>
        </Box>
      );
      break;
    case "signature":
      // SignaturePad provides real drawing in edit mode via FormViewer;
      // in the builder preview we only show the empty area.
      control = (
        <Box
          p="md"
          style={{
            border: "1px dashed var(--mantine-color-gray-3)",
            borderRadius: 8,
            minHeight: 120,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "var(--mantine-color-gray-0)",
          }}
        >
          <Text c="dimmed" fs="italic" size="sm">
            Signature pad
          </Text>
        </Box>
      );
      break;
    default: {
      const _exhaustive: never = field;
      return null;
    }
  }

  return (
    <Box>
      <FieldLabel field={field} />
      {control}
      {error &&
        field.type !== "single_choice" &&
        field.type !== "multiple_choice" && (
          <Text c="red" size="xs" mt={4}>
            {error}
          </Text>
        )}
    </Box>
  );
};
